import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';
import DhcpInserterSubnetEditor from './dhcp-inserter/dhcp-inserter-subnet-editor';

type VlanMode = 'server' | 'relay' | 'inserter' | 'off';
type VidSetting = { vid: number; type: VlanMode };
type CircuitPort = { port_id: number; circuit_id: string };
type InserterSubnet = { vid: number; ports: CircuitPort[] };
type InserterResponse = { subnets?: InserterSubnet[] };
type RemoteIdResponse = { remote_type?: number; remote_id?: string };
type SystemInfo = { name?: string };

function ifaceName(vid: number): string {
  return vid === 0 ? 'Lan' : `VLAN_ID${vid}`;
}

export default function WebUiSettingsDhcpInserterPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [hostname, setHostname] = React.useState('');
  const [remoteType, setRemoteType] = React.useState<number>(0);
  const [remoteId, setRemoteId] = React.useState('');
  const [vids, setVids] = React.useState<VidSetting[]>([]);
  const [subnets, setSubnets] = React.useState<InserterSubnet[]>([]);
  const [openedVid, setOpenedVid] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [info, vidData, inserterData, rid] = await Promise.all([
      postWebUiApi<SystemInfo>({
        service: 'system',
        access: 'get',
        command: 'get info',
      }),
      postWebUiApi<VidSetting[]>({
        service: 'dhcp',
        access: 'get',
        command: 'get vids',
      }),
      postWebUiApi<InserterResponse>({
        service: 'dhcp',
        access: 'get',
        command: 'inserter settings',
      }),
      postWebUiApi<RemoteIdResponse>({
        service: 'dhcp',
        access: 'get',
        command: 'remote id',
      }),
    ]);
    const nextHost = info.name ?? '';
    const nextType = Number(rid.remote_type ?? 0);
    const nextRemoteId =
      nextType === 1 ? nextHost : String(rid.remote_id ?? nextHost);
    setHostname(nextHost);
    setRemoteType(nextType);
    setRemoteId(nextRemoteId);
    setVids(vidData ?? []);
    const nextSubnets = inserterData.subnets ?? [];
    setSubnets(nextSubnets);
    setOpenedVid((prev) => {
      if (!nextSubnets.length) return null;
      if (prev != null && nextSubnets.some((s) => s.vid === prev)) return prev;
      return nextSubnets[0].vid;
    });
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const setRemoteSettings = async () => {
    try {
      const payload = {
        remote_type: remoteType,
        remote_id: remoteType === 1 ? hostname : remoteId,
      };
      await postWebUiApi({
        service: 'dhcp',
        access: 'set',
        command: 'remote id',
        parameters: payload,
      });
      setMessage('System settings updated.');
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const setVidSettings = async () => {
    try {
      await postWebUiApi({
        service: 'dhcp',
        access: 'set',
        command: 'set vids',
        parameters: vids,
      });
      setMessage('DHCP interface settings updated.');
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const setCircuitIds = async (vid: number) => {
    try {
      const subnet = subnets.find((s) => s.vid === vid);
      await postWebUiApi({
        service: 'dhcp',
        access: 'set',
        command: 'inserter update subnet',
        parameters: { vid, ports: subnet?.ports ?? [] },
      });
      setMessage(`Circuit IDs updated for ${ifaceName(vid)}.`);
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <WebUiPageWrapper titleList={['Settings', 'DHCP Inserter']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">DHCP System Settings</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <td>Name</td>
                <td>Setting</td>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>
                  Remote ID Type <br /> (shared with DHCP Relay)
                </td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={[
                      { label: 'Manual', value: '0' },
                      { label: 'Hostname', value: '1' },
                    ]}
                    disabled={readonly}
                    value={String(remoteType)}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setRemoteType(next);
                      if (next === 1) setRemoteId(hostname);
                    }}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Remote ID (shared with DHCP Relay)</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    className="w-full"
                    disabled={readonly || remoteType === 1}
                    value={remoteType === 1 ? hostname : remoteId}
                    onChange={(e) => setRemoteId(e.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton disabled={readonly} onClick={setRemoteSettings}>
              Set System Settings
            </WebUiButton>
          </div>
          <h4 className="mt-4 font-bold">DHCP per Management Interfaces</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <td>Interface</td>
                <td>Status DHCP</td>
              </tr>
            </thead>
            <tbody>
              {vids.map((v) => (
                <tr key={v.vid} className="webui-table-row-content">
                  <td>{ifaceName(v.vid)}</td>
                  <td>
                    <WebUiSelect
                      inputSize="sm"
                      className="w-full"
                      disabled={readonly}
                      value={v.type}
                      onChange={(e) =>
                        setVids((prev) =>
                          prev.map((x) =>
                            x.vid === v.vid
                              ? { ...x, type: e.target.value as VlanMode }
                              : x,
                          ),
                        )
                      }
                      options={[
                        { label: 'Server', value: 'server' },
                        { label: 'Relay', value: 'relay' },
                        { label: 'Inserter', value: 'inserter' },
                        { label: 'Off', value: 'off' },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div>
            <WebUiButton disabled={readonly} onClick={setVidSettings}>
              Set DHCP Interface Settings
            </WebUiButton>
          </div>
        </div>

        <div className="flex w-2/3 flex-col gap-2">
          <h4 className="font-bold">Subnet</h4>
          <DhcpInserterSubnetEditor
            subnets={subnets}
            openedVid={openedVid}
            readonly={readonly}
            setOpenedVid={setOpenedVid}
            setSubnets={setSubnets}
            setCircuitIds={setCircuitIds}
          />
          <div>
            <WebUiButton disabled={readonly} onClick={reload}>
              Reload
            </WebUiButton>
          </div>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
